import { getPublishErrorDescription, PUBLISH_STATUS_KEY } from '@/hooks/queries/useBcpPublishStatus'
import { datatableSearchSchema } from '@/lib/utils/datatableSearchSchema'
import { fetchCsvExport } from '@/lib/utils/exports'
import { getApi } from '@/stores/api'
import {
  AssortmentBackup,
  AssortmentBulkUpdateBody,
  AssortmentUpdateBody,
} from '@repo/backend/assortment'
import { INDETERMINATE, LAST_UPDATED_ASSORTMENT_LOG } from '@repo/constants'
import { toastClassName } from '@repo/ui/lib/constants'
import { getUserFullname } from '@repo/utils'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Dispatch, SetStateAction } from 'react'
import { useTranslation } from 'react-i18next'
import { toast } from 'sonner'
import { z } from 'zod'
import { stringifyValues } from '../../lib/utils/queries'
import { ensureMinimumOperationTime } from '../../lib/utils/timing'
import { useLocale } from '../useLocale'

export const ASSORTMENT_KEY = 'assortment'
export const ASSORTMENT_BACKUPS_KEY = 'assortmentBackups'

const flagSchema = z.union([z.boolean(), z.literal(INDETERMINATE)])

const assortmentSchema = z.object({
  province: z.array(z.string()).optional(),
  account: z.array(z.string()).optional(),
  product: z.array(z.string()).optional(),
  isOrder: flagSchema.optional(),
  isStrategic: flagSchema.optional(),
  isNewProduct: flagSchema.optional(),
  isFeatured: flagSchema.optional(),
  isSellin: flagSchema.optional(),
  isPcapture: flagSchema.optional(),
  isOutOfStock: flagSchema.optional(),
  isUpdate: flagSchema.optional(),
})

export const assortmentSearchSchema = z.object({
  ...assortmentSchema.shape,
  ...datatableSearchSchema.shape,
})

export type AssortmentSearch = z.infer<typeof assortmentSearchSchema>

const activeAssortmentSchema = z.object({
  province: z.array(z.string()).optional(),
  banner: z.array(z.string()).optional(),
  product: z.array(z.string()).optional(),
  erp: z.string().optional(),
  isOrder: flagSchema.optional(),
  isStrategic: flagSchema.optional(),
  isNewProduct: flagSchema.optional(),
  isFeatured: flagSchema.optional(),
  isSellin: flagSchema.optional(),
  isPcapture: flagSchema.optional(),
  isOutOfStock: flagSchema.optional(),
})

export const activeAssortmentSearchSchema = z.object({
  ...activeAssortmentSchema.shape,
  ...datatableSearchSchema.shape,
})

export type ActiveAssortmentSearch = z.infer<typeof activeAssortmentSearchSchema>

export async function getProvinces(signal?: AbortSignal) {
  const api = getApi()
  const res = await api.assortment.facets.provinces.$get({}, { init: { signal } })

  const provinces = await res.json()
  return provinces
}

export async function getAccounts(signal?: AbortSignal) {
  const api = getApi()
  const res = await api.assortment.facets.accounts.$get({}, { init: { signal } })

  const accounts = await res.json()
  return accounts
}

export async function getProducts(signal?: AbortSignal) {
  const api = getApi()
  const res = await api.assortment.facets.products.$get({}, { init: { signal } })

  const products = await res.json()
  return products
}

export async function getAssortment(search: AssortmentSearch, signal?: AbortSignal) {
  const api = getApi()
  const res = await api.assortment.banners.$get(
    {
      query: stringifyValues(search),
    },
    { init: { signal } },
  )

  const assortmentAndRowCount = await res.json()
  return assortmentAndRowCount
}

export async function addAssortmentFromNewMaterials() {
  const api = getApi()
  const res = await api.assortment.materials.sync.$post()

  const message = await res.json()
  return message
}

export async function cleanUpObsoleteAssortment() {
  const api = getApi()
  const res = await api.assortment.materials.$delete()

  const message = await res.json()
  return message
}

export async function updateAssortment(data: AssortmentUpdateBody) {
  const api = getApi()
  const res = await api.assortment.banners.$patch({
    json: data,
  })

  const message = await res.json()
  return message
}

export async function updateAssortmentBulk({
  assortmentIds,
  flags,
  sequence,
  search,
}: AssortmentBulkUpdateBody) {
  const api = getApi()
  const res = await api.assortment.banners.bulk.$patch({
    json: {
      assortmentIds,
      flags,
      sequence,
      search,
    },
  })

  const message = await res.json()
  return message
}

async function publishAssortment() {
  const api = getApi()
  const res = await api.assortment.banners.publish.$post()
  return res.json()
}

export async function getAssortmentBackups(signal?: AbortSignal) {
  const api = getApi()
  const res = await api.assortment.backups.$get({}, { init: { signal } })

  const backups = await res.json()
  return backups
}

export async function createAssortmentBackup(backup: Omit<AssortmentBackup, 'id'>) {
  const api = getApi()
  const res = await api.assortment.backups.$post({ json: backup })

  const response = await res.json()
  return response
}

export async function restoreAssortmentBackup(backup: AssortmentBackup) {
  const api = getApi()
  const res = await api.assortment.backups.$put({ json: backup })

  const backupWithRowCount = await res.json()
  return backupWithRowCount
}

export async function deleteAssortmentBackup(backup: AssortmentBackup) {
  const api = getApi()
  const res = await api.assortment.backups.$delete({ json: backup })

  const deletedBackup = await res.json()
  return deletedBackup
}

async function getAssortmentCsv(search: AssortmentSearch, signal?: AbortSignal) {
  const queryParams = new URLSearchParams(stringifyValues(search)).toString()

  const csvExport = await fetchCsvExport(`/assortment/banners/export?${queryParams}`, signal)

  return csvExport
}

export async function getActiveAssortment(search: ActiveAssortmentSearch, signal?: AbortSignal) {
  const api = getApi()
  const res = await api.assortment.active.$get(
    {
      query: stringifyValues(search),
    },
    { init: { signal } },
  )

  const activeAssortmentAndRowCount = await res.json()
  return activeAssortmentAndRowCount
}

async function getActiveAssortmentCsv(search: ActiveAssortmentSearch, signal?: AbortSignal) {
  delete search.page
  delete search.pageSize
  const queryParams = new URLSearchParams(stringifyValues(search)).toString()

  const csvExport = await fetchCsvExport(`/assortment/active/export?${queryParams}`, signal)

  return csvExport
}

export function assortmentExportQueryOptions(queries: AssortmentSearch) {
  return {
    queryKey: ['assortmentExport', queries],
    queryFn: ({ signal }: { signal: AbortSignal }) => getAssortmentCsv(queries, signal),
  }
}

export function useAssortmentProvinces() {
  return useQuery({
    queryKey: ['assortmentProvinces'],
    queryFn: ({ signal }) => getProvinces(signal),
  })
}

export function useAssortmentAccounts() {
  return useQuery({
    queryKey: ['assortmentAccounts'],
    queryFn: ({ signal }) => getAccounts(signal),
  })
}

export function useAssortmentProducts() {
  return useQuery({
    queryKey: ['assortmentProducts'],
    queryFn: ({ signal }) => getProducts(signal),
  })
}

export function useAssortment(queries: AssortmentSearch) {
  const locale = useLocale()

  return useQuery({
    queryKey: [ASSORTMENT_KEY, queries],
    queryFn: ({ signal }) => getAssortment({ ...queries, locale }, signal),
  })
}

type UseUpdateAssortmentArgs = {
  changesCount: number
  queries: AssortmentSearch
  handleOnSuccess: () => void
  handleOnError?: () => void
}

export function useAddAssortmentFromNewMaterials() {
  const { t } = useTranslation(['assortment', 'common'])
  const queryClient = useQueryClient()

  return useMutation({
    mutationKey: ['syncNewMaterials'],
    mutationFn: addAssortmentFromNewMaterials,
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [ASSORTMENT_KEY],
      })

      queryClient.refetchQueries({
        queryKey: [LAST_UPDATED_ASSORTMENT_LOG],
        type: 'active',
        exact: true,
      })

      toast.success(t('toast.success', { ns: 'common' }), {
        description: t('toast.syncNewMaterials.success'),
        className: toastClassName,
      })
    },
    onError: () => {
      toast.error(t('toast.error', { ns: 'common' }), {
        description: t('toast.syncNewMaterials.error'),
        className: toastClassName,
      })
    },
  })
}

export function useCleanUpObsoleteAssortment() {
  const { t } = useTranslation(['assortment', 'common'])
  const queryClient = useQueryClient()

  return useMutation({
    mutationKey: ['cleanUpObsoleteAssortment'],
    mutationFn: cleanUpObsoleteAssortment,
    onSuccess: ({ rowCount }) => {
      queryClient.invalidateQueries({
        queryKey: [ASSORTMENT_KEY],
      })

      queryClient.invalidateQueries({
        queryKey: [ASSORTMENT_BACKUPS_KEY],
      })

      queryClient.refetchQueries({
        queryKey: [LAST_UPDATED_ASSORTMENT_LOG],
        type: 'active',
        exact: true,
      })

      const translatedMessage =
        rowCount === 0
          ? t('toast.cleanUpAssortment.success.zero')
          : t('toast.cleanUpAssortment.success.other', { count: rowCount })

      toast.success(t('toast.success', { ns: 'common' }), {
        description: translatedMessage,
        className: toastClassName,
      })
    },
    onError: () => {
      toast.error(t('toast.error', { ns: 'common' }), {
        description: t('toast.cleanUpAssortment.error'),
        className: toastClassName,
      })
    },
  })
}

export function useUpdateAssortment({ changesCount, handleOnSuccess }: UseUpdateAssortmentArgs) {
  const { t } = useTranslation(['assortment', 'common'])
  const queryClient = useQueryClient()

  return useMutation({
    mutationKey: ['updateAssortment'],
    mutationFn: updateAssortment,
    onSuccess: () => {
      handleOnSuccess()

      queryClient.invalidateQueries({
        queryKey: [ASSORTMENT_KEY],
      })

      queryClient.refetchQueries({
        queryKey: [LAST_UPDATED_ASSORTMENT_LOG],
        type: 'active',
        exact: true,
      })

      toast.success(t('toast.success', { ns: 'common' }), {
        description: t('toast.updateAssortment.success', { count: changesCount }),
        className: toastClassName,
      })
    },
    onError: () => {
      toast.error(t('toast.error', { ns: 'common' }), {
        description: t('toast.updateAssortment.error'),
        className: toastClassName,
      })
    },
  })
}

export function useUpdateAssortmentBulk(setDialogOpen: Dispatch<SetStateAction<boolean>>) {
  const { t } = useTranslation(['assortment', 'common'])
  const queryClient = useQueryClient()

  return useMutation({
    mutationKey: ['updateAssortmentBulk'],
    mutationFn: async (params: Parameters<typeof updateAssortmentBulk>[0]) => {
      const startTime = Date.now()
      const result = await updateAssortmentBulk(params)
      await ensureMinimumOperationTime(startTime)

      return result
    },
    onSuccess: ({ message }) => {
      queryClient.invalidateQueries({
        queryKey: [ASSORTMENT_KEY],
      })

      queryClient.refetchQueries({
        queryKey: [LAST_UPDATED_ASSORTMENT_LOG],
        type: 'active',
        exact: true,
      })

      setDialogOpen(false)

      toast.success(t('toast.success', { ns: 'common' }), {
        description: message,
        className: toastClassName,
      })
    },
    onError: () => {
      toast.error(t('toast.error', { ns: 'common' }), {
        description: t('toast.updateAssortment.error'),
        className: toastClassName,
      })
    },
  })
}

export function useAssortmentBackups() {
  return useQuery({
    queryKey: [ASSORTMENT_BACKUPS_KEY],
    queryFn: async ({ signal }) => {
      const backups = await getAssortmentBackups(signal)
      return backups.map(({ givenName, familyName, fallbackName, ...backup }) => ({
        ...backup,
        createdBy: getUserFullname({ givenName, familyName, fallbackName }),
      }))
    },
  })
}

export function useCreateAssortmentBackup() {
  const { t } = useTranslation(['assortment', 'common'])
  const queryClient = useQueryClient()

  return useMutation({
    mutationKey: ['createAssortmentBackup'],
    mutationFn: createAssortmentBackup,
    onSuccess: ({ backup }: { backup: AssortmentBackup }) => {
      queryClient.invalidateQueries({
        queryKey: [ASSORTMENT_BACKUPS_KEY],
      })

      toast.success(t('toast.success', { ns: 'common' }), {
        description: t('toast.createBackup.success', { name: backup.name }),
        className: toastClassName,
      })
    },
    onError: () => {
      toast.error(t('toast.error', { ns: 'common' }), {
        description: t('toast.createBackup.error'),
        className: toastClassName,
      })
    },
  })
}

export function useRestoreAssortmentBackup() {
  const { t } = useTranslation(['assortment', 'common'])
  const queryClient = useQueryClient()

  return useMutation({
    mutationKey: ['restoreAssortmentBackup'],
    mutationFn: restoreAssortmentBackup,
    onSuccess: ({ backup, rowCount }: { backup: AssortmentBackup; rowCount: number }) => {
      queryClient.invalidateQueries({
        queryKey: [ASSORTMENT_KEY],
      })

      queryClient.refetchQueries({
        queryKey: [LAST_UPDATED_ASSORTMENT_LOG],
        type: 'active',
        exact: true,
      })

      toast.success(t('toast.success', { ns: 'common' }), {
        description: t('toast.restoreBackup.success', {
          name: backup.name,
          count: rowCount,
        }),
        className: toastClassName,
      })
    },
    onError: () => {
      toast.error(t('toast.error', { ns: 'common' }), {
        description: t('toast.restoreBackup.error'),
        className: toastClassName,
      })
    },
  })
}

export function useDeleteAssortmentBackup() {
  const { t } = useTranslation(['assortment', 'common'])
  const queryClient = useQueryClient()

  return useMutation({
    mutationKey: ['deleteAssortmentBackup'],
    mutationFn: deleteAssortmentBackup,
    onSuccess: ({ name }) => {
      queryClient.invalidateQueries({
        queryKey: [ASSORTMENT_BACKUPS_KEY],
      })

      toast.success(t('toast.success', { ns: 'common' }), {
        description: t('toast.deleteBackup.success', { name }),
        className: toastClassName,
      })
    },
    onError: () => {
      toast.error(t('toast.error', { ns: 'common' }), {
        description: t('toast.deleteBackup.error'),
        className: toastClassName,
      })
    },
  })
}

export function usePublishAssortment() {
  const { t } = useTranslation('common')
  const queryClient = useQueryClient()

  return useMutation({
    mutationKey: ['publishAssortment'],
    mutationFn: publishAssortment,
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [PUBLISH_STATUS_KEY, ASSORTMENT_KEY],
      })

      toast.info(t('toast.info'), {
        description: t('publish.initiated', { publishType: t('publish.type.assortment') }),
      })
    },
    onError: (error) => {
      const description = getPublishErrorDescription(error, t, t('publish.type.assortment'))
      toast.error(t('toast.error'), {
        description,
        className: toastClassName,
      })
    },
  })
}

export function useActiveAssortment(queries: ActiveAssortmentSearch) {
  return useQuery({
    queryKey: ['activeAssortment', queries],
    queryFn: ({ signal }) => getActiveAssortment(queries, signal),
  })
}

export function activeAssortmentExportQueryOptions(queries: ActiveAssortmentSearch) {
  return {
    queryKey: ['activeAssortmentExport', queries],
    queryFn: ({ signal }: { signal: AbortSignal }) => getActiveAssortmentCsv(queries, signal),
  }
}
